import { useState, useEffect, useRef, useCallback } from "react";
import { GoogleMap, useJsApiLoader, Marker, Polyline, InfoWindow } from "@react-google-maps/api";
import { API , authFetch, useAuth } from "@/App";
import { mutate } from "@/lib/apiMutate";
import { toast } from "sonner";
import { SyncNowButton } from "@/components/SyncNowButton";
import { RegionSwitcher } from "@/components/RegionSwitcher";
import { useRegionFilter } from "@/hooks/useRegionFilter";
import {
  Map, Plus, Search, MoreHorizontal, Edit, MapPin, Route, Trash2, Navigation,
  CheckSquare, Square, Loader2, Users
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";

const DISTRICTS_GJ = ["Banaskantha", "Sabarkantha", "Mehsana", "Anand", "Kheda", "Gandhinagar", "Ahmedabad", "Vadodara", "Surat", "Rajkot"];
const DISTRICTS_RJ = ["Jaipur", "Jodhpur", "Udaipur", "Kota", "Ajmer", "Bikaner"];
const ALL_DISTRICTS = [...DISTRICTS_GJ, ...DISTRICTS_RJ];

const BEATsPage = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "SUPER_ADMIN";
  const { activeRegion, setActiveRegion, filterByRegion } = useRegionFilter();
  const [beats, setBeats] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [districtFilter, setDistrictFilter] = useState("all");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingBeat, setEditingBeat] = useState(null);
  const [optimizedRoute, setOptimizedRoute] = useState(null);
  const [optimizing, setOptimizing] = useState(false);
  const [selectedBeat, setSelectedBeat] = useState(null);
  const [mapError, setMapError] = useState(false);
  const [dealerSearch, setDealerSearch] = useState("");
  const mapRef = useRef(null);
  const [formData, setFormData] = useState({
    beat_code: "", beat_name: "", district: "", state: "Gujarat",
    notes: "", assigned_employee_ids: [], dealer_ids: [],
  });

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_KEY || "",
  });

  useEffect(() => {
    window.gm_authFailure = () => setMapError(true);
    return () => { window.gm_authFailure = undefined; };
  }, []);

  useEffect(() => { fetchData(); }, [districtFilter]);

  const fetchData = async () => {
    try {
      let url = `${API}/beats?`;
      if (districtFilter && districtFilter !== "all") url += `district=${districtFilter}&`;
      const [beatsRes, employeesRes, dealersRes] = await Promise.all([
        authFetch(url, {  }),
        authFetch(`${API}/employees?status=ACTIVE`, {  }),
        authFetch(`${API}/dealers/map`, {  }),
      ]);
      const beatsData = await beatsRes.json();
      const employeesData = await employeesRes.json();
      const dealersData = await dealersRes.json();
      if (beatsData.success) setBeats(beatsData.data);
      if (employeesData.success) setEmployees(employeesData.data);
      if (dealersData.success) setDealers(dealersData.data || []);
    } catch (error) { console.error("Failed to fetch data:", error); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const url = editingBeat ? `${API}/beats/${editingBeat.beat_id}` : `${API}/beats`;
    const r = await mutate(
      authFetch,
      url,
      {
        method: editingBeat ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      },
      {
        successMessage: editingBeat ? "BEAT updated" : "BEAT created",
        failureMessage: "Failed to save BEAT",
      },
    );
    if (!r.ok) return;
    setIsDialogOpen(false); setEditingBeat(null); resetForm(); fetchData();
  };

  const openEditDialog = (beat) => {
    setEditingBeat(beat);
    setFormData({
      beat_code: beat.beat_code, beat_name: beat.beat_name,
      district: beat.district, state: beat.state,
      notes: beat.notes || "", assigned_employee_ids: beat.assigned_employee_ids || [],
      dealer_ids: beat.dealer_ids || [],
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => setFormData({
    beat_code: "", beat_name: "", district: "", state: "Gujarat",
    notes: "", assigned_employee_ids: [], dealer_ids: [],
  });

  const toggleDealer = (dealerId) => {
    setFormData(prev => ({
      ...prev,
      dealer_ids: prev.dealer_ids.includes(dealerId)
        ? prev.dealer_ids.filter(id => id !== dealerId)
        : [...prev.dealer_ids, dealerId],
    }));
  };

  const toggleEmployee = (empId) => {
    setFormData(prev => ({
      ...prev,
      assigned_employee_ids: prev.assigned_employee_ids.includes(empId)
        ? prev.assigned_employee_ids.filter(id => id !== empId)
        : [...prev.assigned_employee_ids, empId],
    }));
  };

  const handleOptimizeRoute = async (beat) => {
    if (!beat.dealer_ids?.length) { toast.error("No dealers assigned to this BEAT"); return; }
    setOptimizing(true);
    setSelectedBeat(beat.beat_id);
    try {
      const res = await authFetch(`${API}/beats/optimize-route`, {
        method: "POST", headers: { "Content-Type": "application/json" },
         body: JSON.stringify({ dealer_ids: beat.dealer_ids }),
      });
      const data = await res.json();
      if (data.success) {
        setOptimizedRoute(data.data);
        toast.success(`Route optimized: ${data.data.total_distance_km} km, ${data.data.dealer_count} dealers`);
      } else toast.error(data.detail || "Optimization failed");
    } catch { toast.error("Failed to optimize route"); }
    finally { setOptimizing(false); }
  };

  const showMap = isLoaded && !loadError && !mapError;
  const mapCenter = dealers.length > 0
    ? { lat: dealers.reduce((s, d) => s + d.lat, 0) / dealers.length, lng: dealers.reduce((s, d) => s + d.lng, 0) / dealers.length }
    : { lat: 23.0, lng: 72.6 };

  const filteredDialogDealers = dealers.filter(d =>
    !dealerSearch || d.firm_name?.toLowerCase().includes(dealerSearch.toLowerCase()) || d.district?.toLowerCase().includes(dealerSearch.toLowerCase())
  );

  // Region-filtered slice for stats + list. Beats may have a state field
  // ("Gujarat"/"Rajasthan") instead of region_id, so support both.
  const regionBeats = filterByRegion(beats, (b) =>
    b.region_id || (b.state === "Gujarat" ? "GJ" : b.state === "Rajasthan" ? "RJ" : null)
  );

  return (
    <div className="space-y-4" data-testid="beats-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Sora']">Sales BEATs</h1>
          <p className="text-slate-500 text-sm mt-1">Manage geographic sales territories with route optimization</p>
        </div>
        <div className="flex items-center gap-2">
          <RegionSwitcher
            active={activeRegion}
            onChange={setActiveRegion}
            isSuperAdmin={isSuperAdmin}
            dataTestId="beats-region-switcher"
          />
          <SyncNowButton entity="beats" />
          <Button onClick={() => { setEditingBeat(null); resetForm(); setIsDialogOpen(true); }}
            className="bg-[#8B1A1A] hover:bg-[#6B1515] text-white" data-testid="add-beat-btn">
            <Plus className="w-4 h-4 mr-2" /> Create BEAT
          </Button>
        </div>
      </div>

      {/* Stats + Filter */}
      <div className="flex items-center gap-4">
        <Card className="px-4 py-3 flex-1">
          <p className="text-[10px] uppercase text-slate-400 font-semibold">Total</p>
          <p className="text-xl font-bold font-mono text-slate-900">{regionBeats.length}</p>
        </Card>
        <Card className="px-4 py-3 flex-1">
          <p className="text-[10px] uppercase text-slate-400 font-semibold">Districts</p>
          <p className="text-xl font-bold font-mono text-slate-900">{new Set(regionBeats.map(b => b.district)).size}</p>
        </Card>
        <Card className="px-4 py-3 flex-1">
          <p className="text-[10px] uppercase text-slate-400 font-semibold">Active</p>
          <p className="text-xl font-bold font-mono text-emerald-600">{regionBeats.filter(b => b.status === "ACTIVE").length}</p>
        </Card>
        <Card className="px-4 py-3 flex-1" data-testid="stat-mobile-pending">
          <p className="text-[10px] uppercase text-slate-400 font-semibold">Pending (Mobile)</p>
          <p className="text-xl font-bold font-mono text-amber-600">{regionBeats.filter(b => b.source === "mobile" && b.status === "PENDING").length}</p>
        </Card>
        <Select value={districtFilter} onValueChange={setDistrictFilter}>
          <SelectTrigger className="w-[160px]" data-testid="district-filter">
            <SelectValue placeholder="All Districts" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Districts</SelectItem>
            {ALL_DISTRICTS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Map + Optimized Route */}
      <Card className="overflow-hidden" data-testid="beat-map">
        <div className="px-4 py-2 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h3 className="text-xs font-semibold text-slate-900 uppercase tracking-wider">BEAT Map</h3>
            {optimizedRoute && (
              <div className="flex items-center gap-2 text-[10px]">
                <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200">
                  <Route className="w-3 h-3 mr-1" /> {optimizedRoute.total_distance_km} km · {optimizedRoute.dealer_count} stops · ~{optimizedRoute.estimated_time_hours}h
                </Badge>
              </div>
            )}
          </div>
          {optimizedRoute && <Button variant="ghost" size="sm" onClick={() => setOptimizedRoute(null)} className="text-xs">Clear Route</Button>}
        </div>
        <div style={{ height: "320px" }}>
          {showMap ? (
            <GoogleMap mapContainerStyle={{ width: "100%", height: "100%", borderRadius: "0 0 12px 12px" }}
              center={mapCenter} zoom={8}
              onLoad={map => { mapRef.current = map; }}
              options={{ styles: [{ featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] }], streetViewControl: false }}>
              {/* Dealer markers */}
              {dealers.map(d => {
                const inBeat = selectedBeat && beats.find(b => b.beat_id === selectedBeat)?.dealer_ids?.includes(d.party_id);
                return (
                  <Marker key={d.party_id} position={{ lat: d.lat, lng: d.lng }}
                    icon={{
                      path: window.google?.maps?.SymbolPath?.BACKWARD_CLOSED_ARROW || 3,
                      scale: inBeat ? 8 : 5, fillColor: inBeat ? "#F59E0B" : "#3B82F6",
                      fillOpacity: inBeat ? 1 : 0.6, strokeColor: "#fff", strokeWeight: 1.5,
                    }}
                    title={d.firm_name} />
                );
              })}
              {/* Optimized route polyline */}
              {optimizedRoute?.route?.length > 1 && (
                <Polyline
                  path={optimizedRoute.route.map(s => ({ lat: s.lat, lng: s.lng }))}
                  options={{ strokeColor: "#8B1A1A", strokeWeight: 4, strokeOpacity: 0.9, icons: [{ icon: { path: window.google?.maps?.SymbolPath?.FORWARD_CLOSED_ARROW || 1, scale: 3, fillColor: "#8B1A1A", fillOpacity: 1 }, offset: "100%", repeat: "80px" }] }}
                />
              )}
              {/* Route stop markers */}
              {optimizedRoute?.route?.map((stop, i) => (
                <Marker key={`stop-${i}`} position={{ lat: stop.lat, lng: stop.lng }}
                  label={{ text: `${i + 1}`, color: "#fff", fontSize: "10px", fontWeight: "bold" }}
                  icon={{ path: window.google?.maps?.SymbolPath?.CIRCLE || 0, scale: 12, fillColor: "#8B1A1A", fillOpacity: 1, strokeColor: "#fff", strokeWeight: 2 }}
                  title={`${i + 1}. ${stop.firm_name} (${stop.distance_from_prev_km} km)`} />
              ))}
            </GoogleMap>
          ) : (
            <div className="flex items-center justify-center h-full text-slate-400 text-sm">
              <MapPin className="w-6 h-6 mr-2" /> Loading map...
            </div>
          )}
        </div>
      </Card>

      {/* BEATs Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="w-8 h-8 border-2 border-[#8B1A1A] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : beats.length === 0 ? (
        <Card className="p-8 text-center">
          <Map className="w-10 h-10 mx-auto mb-3 text-slate-300" />
          <p className="text-sm text-slate-500">No BEATs found</p>
          <Button onClick={() => setIsDialogOpen(true)} className="mt-3 bg-[#8B1A1A] hover:bg-[#6B1515] text-white">
            <Plus className="w-4 h-4 mr-2" /> Create BEAT
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {regionBeats.map(beat => {
            const assignedEmployees = employees.filter(e => beat.assigned_employee_ids?.includes(e.employee_id));
            const isSelected = selectedBeat === beat.beat_id;
            return (
              <Card key={beat.beat_id}
                className={`p-5 hover:shadow-md transition-all cursor-pointer ${isSelected ? "border-[#8B1A1A] border-2" : ""}`}
                data-testid={`beat-card-${beat.beat_id}`}
                onClick={() => setSelectedBeat(isSelected ? null : beat.beat_id)}>
                <div className="flex items-start justify-between mb-3">
                  <div className="p-2 rounded-lg bg-blue-50">
                    <Map className="w-4 h-4 text-blue-600" />
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" data-testid={`beat-menu-${beat.beat_id}`}>
                        <MoreHorizontal className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openEditDialog(beat); }}>
                        <Edit className="w-3.5 h-3.5 mr-2" /> Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleOptimizeRoute(beat); }}
                        disabled={!beat.dealer_ids?.length}>
                        <Route className="w-3.5 h-3.5 mr-2" /> Optimize Route
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <p className="text-[10px] text-slate-400 font-mono">{beat.beat_code}</p>
                <h3 className="text-sm font-semibold text-slate-900">{beat.beat_name}</h3>
                {beat.source === "mobile" && (
                  <div className="mt-1 flex items-center gap-1">
                    <Badge variant="outline" className="text-[9px] border-blue-200 text-blue-700 bg-blue-50" data-testid={`source-mobile-${beat.beat_id}`}>
                      Created on mobile by {beat.created_by_employee_name || "field team"}
                    </Badge>
                  </div>
                )}
                <div className="grid grid-cols-3 gap-2 mt-3 text-[10px]">
                  <div><span className="text-slate-400">District</span><p className="font-medium text-slate-700">{beat.district}</p></div>
                  <div><span className="text-slate-400">Dealers</span><p className="font-mono font-medium text-slate-700">{beat.dealer_ids?.length || beat.dealer_count || 0}</p></div>
                  <div><span className="text-slate-400">Team</span><p className="font-mono font-medium text-slate-700">{beat.assigned_employee_ids?.length || 0}</p></div>
                </div>
                {assignedEmployees.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-slate-100">
                    <div className="flex flex-wrap gap-1">
                      {assignedEmployees.slice(0, 3).map(emp => (
                        <span key={emp.employee_id} className="px-1.5 py-0.5 bg-slate-100 rounded text-[10px] text-slate-600">{emp.full_name}</span>
                      ))}
                      {assignedEmployees.length > 3 && <span className="text-[10px] text-slate-400">+{assignedEmployees.length - 3}</span>}
                    </div>
                  </div>
                )}
                <div className="mt-3 flex items-center justify-between">
                  <Badge variant="outline" className={
                    beat.status === "ACTIVE" ? "text-emerald-600 border-emerald-200 bg-emerald-50 text-[10px]" :
                    beat.status === "PENDING" ? "text-amber-600 border-amber-200 bg-amber-50 text-[10px]" :
                    "text-[10px]"
                  }>
                    {beat.status}
                  </Badge>
                  {beat.dealer_ids?.length > 0 && (
                    <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px] text-[#8B1A1A]"
                      onClick={(e) => { e.stopPropagation(); handleOptimizeRoute(beat); }}
                      disabled={optimizing} data-testid={`optimize-${beat.beat_id}`}>
                      {optimizing && selectedBeat === beat.beat_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Route className="w-3 h-3 mr-1" />}
                      Optimize
                    </Button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Optimized Route Details */}
      {optimizedRoute && (
        <Card className="p-4" data-testid="route-details">
          <h3 className="text-xs font-semibold text-slate-900 uppercase tracking-wider mb-3">Optimized Visit Sequence</h3>
          <div className="space-y-2">
            {optimizedRoute.route.map((stop, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-[#8B1A1A] text-white flex items-center justify-center text-[10px] font-bold shrink-0">{i + 1}</div>
                <div className="flex-1">
                  <p className="text-xs font-semibold text-slate-800">{stop.firm_name}</p>
                  <p className="text-[10px] text-slate-400">{stop.district}</p>
                </div>
                <span className="text-xs font-mono text-slate-500">{stop.distance_from_prev_km} km</span>
                {i < optimizedRoute.route.length - 1 && <Navigation className="w-3 h-3 text-slate-300 rotate-90" />}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="beat-dialog">
          <DialogHeader>
            <DialogTitle>{editingBeat ? "Edit BEAT" : "Create BEAT"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-600">BEAT Code</label>
                <Input value={formData.beat_code} onChange={e => setFormData({ ...formData, beat_code: e.target.value })} placeholder="BANAS-02" required className="h-8 text-sm" data-testid="beat-code-input" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">BEAT Name</label>
                <Input value={formData.beat_name} onChange={e => setFormData({ ...formData, beat_name: e.target.value })} placeholder="Banaskantha South" required className="h-8 text-sm" data-testid="beat-name-input" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">District</label>
                <Select value={formData.district} onValueChange={v => setFormData({ ...formData, district: v })}>
                  <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{ALL_DISTRICTS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">State</label>
                <Input value={formData.state} onChange={e => setFormData({ ...formData, state: e.target.value })} className="h-8 text-sm" />
              </div>
            </div>

            {/* Employee Assignment */}
            <div>
              <label className="text-xs font-medium text-slate-600 flex items-center gap-1">
                <Users className="w-3 h-3" /> Assign Employees ({formData.assigned_employee_ids.length})
              </label>
              <div className="mt-1 border border-slate-200 rounded-lg max-h-[120px] overflow-y-auto p-1.5 space-y-0.5">
                {employees.map(emp => {
                  const isChecked = formData.assigned_employee_ids.includes(emp.employee_id);
                  return (
                    <button key={emp.employee_id} type="button" onClick={() => toggleEmployee(emp.employee_id)}
                      className={`w-full flex items-center gap-2 px-2 py-1 rounded text-left transition-colors ${isChecked ? "bg-blue-50" : "hover:bg-slate-50"}`}>
                      {isChecked ? <CheckSquare className="w-3.5 h-3.5 text-blue-600 shrink-0" /> : <Square className="w-3.5 h-3.5 text-slate-300 shrink-0" />}
                      <span className="text-xs text-slate-700">{emp.full_name}</span>
                      <span className="text-[9px] text-slate-400 ml-auto">{emp.role?.replace(/_/g, " ")}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Dealer Assignment */}
            <div>
              <label className="text-xs font-medium text-slate-600 flex items-center gap-1">
                <MapPin className="w-3 h-3" /> Assign Dealers ({formData.dealer_ids.length})
              </label>
              <Input placeholder="Search dealers..." value={dealerSearch} onChange={e => setDealerSearch(e.target.value)}
                className="h-7 text-xs mt-1" data-testid="dealer-search-dialog" />
              <div className="mt-1 border border-slate-200 rounded-lg max-h-[160px] overflow-y-auto p-1.5 space-y-0.5">
                {filteredDialogDealers.map(d => {
                  const isChecked = formData.dealer_ids.includes(d.party_id);
                  return (
                    <button key={d.party_id} type="button" onClick={() => toggleDealer(d.party_id)}
                      className={`w-full flex items-center gap-2 px-2 py-1 rounded text-left transition-colors ${isChecked ? "bg-amber-50" : "hover:bg-slate-50"}`}
                      data-testid={`toggle-dealer-${d.party_id}`}>
                      {isChecked ? <CheckSquare className="w-3.5 h-3.5 text-amber-600 shrink-0" /> : <Square className="w-3.5 h-3.5 text-slate-300 shrink-0" />}
                      <span className="text-xs text-slate-700 truncate">{d.firm_name}</span>
                      <span className="text-[9px] text-slate-400 ml-auto shrink-0">{d.district}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="text-xs font-medium text-slate-600">Notes</label>
              <Textarea value={formData.notes} onChange={e => setFormData({ ...formData, notes: e.target.value })} placeholder="Additional notes..." rows={2} className="text-sm" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="text-sm">Cancel</Button>
              <Button type="submit" className="bg-[#8B1A1A] hover:bg-[#6B1515] text-white text-sm">{editingBeat ? "Update" : "Create"} BEAT</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BEATsPage;
